import { useBemCN } from '@gik/core/utils/bemBlock';
import type { RasterAttachImageResult } from '@gik/core/utils/FileUtils';
import FileUtils from '@gik/core/utils/FileUtils';
import ImageUtils from '@gik/core/utils/ImageUtils';
import noop from '@gik/core/utils/noop';
import { renderPortalWithRef } from '@gik/core/utils/RenderPortal';
import { Button } from '@gik/ui/Button';
import { Image } from '@gik/ui/Image';
import type { IImageCropData } from '@gik/ui/ImageCropper';
import { ImageCropper } from '@gik/ui/ImageCropper';
import { ModalButtonFooter } from '@gik/ui/Modal';
import { UI } from '@gik/ui/UIManager';
import React from 'react';

export interface IImagePickerModalProps {
  fallback?: string;
  footerRef?: React.MutableRefObject<HTMLElement>;
  text?: string;
  onChange?: (croppedImage: string) => void;
}

export function ImagePickerModal({
  fallback,
  footerRef,
  onChange = noop,
  text = 'Upload a photo.',
}: IImagePickerModalProps) {
  const bem = useBemCN('image-picker-modal');

  const [attachResult, setAttachResult] = React.useState<RasterAttachImageResult>(null);
  const [image, setImage] = React.useState<IImageCropData>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  async function uploadPhoto() {
    setLoading(true);
    setAttachResult((await FileUtils.attachImage()) as RasterAttachImageResult);
    setLoading(false);
  }

  function handleChange(imageData: IImageCropData) {
    setImage(imageData);
  }

  async function save() {
    const croppedImage = await ImageUtils.getCroppedImage(image.image, image.croppedAreaPixels, image.rotation);
    onChange(croppedImage);
    UI.closeDialog();
  }

  function cancel() {
    UI.closeDialog();
  }

  const buttons = (
    <>
      <Button disabled={!image} onClick={save}>
        Save Photo
      </Button>
      <Button variant={'default'} onClick={cancel}>
        Cancel
      </Button>
    </>
  );

  // FIXME: can we use the renderPortal function here?
  return (
    <div {...bem()}>
      <div {...bem('content-wrapper')}>
        <ImageCropper
          aspect={1}
          image={attachResult}
          fallback={fallback && <Image src={fallback} />}
          onChange={handleChange}
        />
        <div {...bem('controls')}>
          <p>{text}</p>
          <Button onClick={uploadPhoto} loading={loading}>
            Upload Photo
          </Button>
        </div>
      </div>

      {renderPortalWithRef(buttons, footerRef)}
    </div>
  );
}

export interface IOpenImagePickerModalProps extends Omit<IImagePickerModalProps, 'footerRef'> {
  title?: string;
}

export function openImagePickerModal(props: IOpenImagePickerModalProps) {
  return UI.dialog(
    ({ footerRef }) => (
      <ImagePickerModal text={props.text} fallback={props.fallback} footerRef={footerRef} onChange={props.onChange} />
    ),
    {
      title: props.title,
      footer: <ModalButtonFooter />,
    }
  );
}
